import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Plus } from 'lucide-react';
import AppShell from '../layouts/AppShell';
import NivaLogo from '../components/NivaLogo';
import UserProfileButton from '../components/UserProfileButton';
import ShiftGate from '../components/ShiftGate';
import PatientListCard from '../components/PatientListCard';
import PatientSearch, { WardChips, SummaryToggle } from '../components/PatientFilters';
import EmptyState from '../components/EmptyState';
import AddPatientModal from '../components/AddPatientModal';
import { Users } from 'lucide-react';
import { WARDS, getPatientDisplayName, getAllPatients } from '../constants/mockPatients';
import { patientAdmissionPath } from '../constants/routes';
import { useSession } from '../contexts/SessionContext';
import { useAdmissionAmbient } from '../contexts/AdmissionAmbientContext';
import { useShift } from '../contexts/ShiftContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translateWard } from '../i18n/translations';
import { ADMISSIONS_ENABLED } from '../constants/features';

function getInitials(patients, language) {
  return patients.map((p) => getPatientDisplayName(p, language).charAt(0).toUpperCase());
}

export default function PatientsListScreen() {
  const {
    isFavorite,
    toggleFavorite,
    admittedPatients,
    addAdmittedPatient,
    ensureDefaultRecurringTasks,
  } = useSession();
  const { beginAdmission } = useAdmissionAmbient();
  const { isOnShift } = useShift();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeWard, setActiveWard] = useState('All Wards');
  const [view, setView] = useState('all');
  const [addPatientOpen, setAddPatientOpen] = useState(false);

  const allPatients = useMemo(
    () => getAllPatients(admittedPatients),
    [admittedPatients]
  );

  const filtered = useMemo(() => {
    let list = allPatients;
    if (view === 'favorites') {
      list = list.filter((p) => isFavorite(p.id));
    }
    if (activeWard !== 'All Wards') {
      list = list.filter((p) => p.ward === activeWard);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.nameEn.toLowerCase().includes(q) ||
          p.name.includes(q) ||
          p.room.includes(q)
      );
    }
    return list;
  }, [view, activeWard, search, isFavorite, allPatients]);

  const favoriteCount = allPatients.filter((p) => isFavorite(p.id)).length;
  const favoritePatients = allPatients.filter((p) => isFavorite(p.id));
  const allInitials = getInitials(allPatients, language);
  const favoriteInitials = getInitials(favoritePatients, language);

  const handleStartAdmission = ({ name, dob, sex, wardType }) => {
    const patient = addAdmittedPatient({ name, dob, sex, wardType });
    beginAdmission(patient.id, { name, dob, sex });
    if (isOnShift) {
      ensureDefaultRecurringTasks(patient, new Date());
    }
    setAddPatientOpen(false);
    navigate(patientAdmissionPath(patient.id));
  };

  return (
    <AppShell showFab={isOnShift}>
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-surface px-4 py-3">
        <div className="flex items-center justify-between">
          <NivaLogo variant="header" />
          <div className="flex items-center gap-2">
            {isOnShift && ADMISSIONS_ENABLED && (
              <button
                type="button"
                onClick={() => setAddPatientOpen(true)}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white"
                aria-label={t('admitNewPatient')}
              >
                <Plus className="h-5 w-5" strokeWidth={2.5} />
              </button>
            )}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-light text-primary"
            >
              <RefreshCw className="h-5 w-5" strokeWidth={2} />
            </button>
            <UserProfileButton />
          </div>
        </div>
      </header>

      <ShiftGate>
        <div className="space-y-4 px-4 py-4">
          <div className="flex gap-3">
            <SummaryToggle
              active={view === 'all'}
              label={t('all')}
              count={allPatients.length}
              onClick={() => setView('all')}
              avatarInitials={allInitials}
            />
            <SummaryToggle
              active={view === 'favorites'}
              label={t('favorites')}
              count={favoriteCount}
              onClick={() => setView('favorites')}
              isFavorite
              avatarInitials={favoriteInitials}
            />
          </div>

          <PatientSearch value={search} onChange={setSearch} placeholder={t('filterByName')} />
          <WardChips
            wards={WARDS}
            activeWard={activeWard}
            onSelect={setActiveWard}
            translateWard={(ward) => translateWard(t, ward)}
          />

          <div className="space-y-3">
            {filtered.length === 0 ? (
              <EmptyState icon={Users} title={t('noPatientsFound')} />
            ) : (
              filtered.map((patient) => (
                <PatientListCard
                  key={patient.id}
                  patient={patient}
                  favorite={isFavorite(patient.id)}
                  onToggleFavorite={toggleFavorite}
                />
              ))
            )}
          </div>
        </div>
      </ShiftGate>

      {ADMISSIONS_ENABLED && (
        <AddPatientModal
          open={addPatientOpen}
          onClose={() => setAddPatientOpen(false)}
          onStartAdmission={handleStartAdmission}
        />
      )}
    </AppShell>
  );
}
